import Link from '@alltrails/shared/denali/components/Link';
import Typography from '@alltrails/shared/denali/components/Typography';
import { PressReleaseCard } from 'components/press/PressCard/PressCard';
import { FormattedMessage } from '@alltrails/shared/react-intl';

const alltrailsLink = (
  <Link href="/" target="_blank">
    AllTrails
  </Link>
);

const spectrumEquityLink = (
  <Link href="https://www.spectrumequity.com/" target="_blank">
    Spectrum Equity
  </Link>
);

const iosLink = (
  <Link href="https://apps.apple.com/us/app/alltrails-hike-bike-run/id405075943" target="_blank">
    iOS
  </Link>
);

const androidLink = (
  <Link href="https://play.google.com/store/apps/details?id=com.alltrails.alltrails" target="_blank">
    Android
  </Link>
);

const pressLink = <Link href="mailto:press@alltrails.com">press@alltrails.com</Link>;

const pressReleases: PressReleaseCard[] = [
  {
    cardType: 'press-release',
    title: <FormattedMessage defaultMessage="AllTrails Unveils New Brand Identity to Reflect a More Inclusive & Connected Outdoor Community" />,
    date: 'January 25, 2023',
    modalContent: (
      <>
        <Typography variant="heading400" component="h2">
          <FormattedMessage defaultMessage="AllTrails Unveils New Brand Identity to Reflect a More Inclusive & Connected Outdoor Community" />
        </Typography>
        <Typography variant="heading200" component="h3">
          <FormattedMessage defaultMessage="‘The Outside in All of Us’ dispels the myth that the outdoors is only meant for the ‘outdoorsy’" />
        </Typography>
        <Typography variant="paragraph100">
          <FormattedMessage
            defaultMessage="{alltrailsLink},  the definitive digital trail expert and your companion to the outdoors, is kicking off the year by 
            unveiling a new brand identity - ‘The Outside in All of Us’ - inviting everyone, regardless of expertise or background, to get outside."
            values={{ alltrailsLink }}
          />
          <br />
          <FormattedMessage
            defaultMessage="AllTrails’ refreshed brand identity was inspired by the company’s belief that the outside isn’t a place to seek, 
          but rather a part of each and every one of us. Design elements evoke the beauty and diversity of global outdoor landscapes, activities, and communities."
          />
          <br />
          <FormattedMessage defaultMessage="As part of the brand’s refresh, AllTrails is launching:" />
          <br />
          <li>
            <b>
              <FormattedMessage defaultMessage="A refreshed visual identity" />
            </b>
            <FormattedMessage defaultMessage=", including a new symbol, wordmark, typography, graphic elements, art direction and color palette" />
          </li>
          <li>
            <b>
              <FormattedMessage defaultMessage="New tone and messaging" />
            </b>
            <FormattedMessage defaultMessage=", invoking the persona of your adventurous friend; inspiring, motivating, adventurous, trustworthy, and never intimidating" />
          </li>
          <li>
            <b>
              <FormattedMessage defaultMessage="Rebranded premium offering “AllTrails Pro” to “AllTrails+”" />
            </b>
            <FormattedMessage defaultMessage="making it clear that AllTrails+ isn’t about who scales the highest mountains – it’s about giving everyone extra tools to save time, be prepared, and do more outdoors." />
          </li>
          <br />
          <FormattedMessage
            defaultMessage="“We’re building a movement to redefine the outdoors, remove hurdles in the path, and help everyone find a sense of belonging in the outdoors” said Carly Smith Costello, CMO of AllTrails.  
          “The time has come to reflect this in our brand. We’re re-introducing ourselves with an inviting  brand identity - a fresh new look and feel and a new brand for our premium offering,” she added."
          />
          <br />
          <FormattedMessage
            defaultMessage="The brand evolution is now live in the AllTrails app and at {alltrailsLink}."
            values={{ alltrailsLink }}
          />
          <br />
          <FormattedMessage
            defaultMessage="In addition to the new branding, AllTrails has also expanded its Community feature, building a space for the brand’s over 45 million users to connect and inspire one another. 
          The community feature helps users explore and discover even more trails and outdoor spaces, all through a user’s network of connections."
          />
          <br />
          <FormattedMessage defaultMessage="All of these Community features are available to members today, completely free of charge." />
          <br />
          <FormattedMessage
            defaultMessage="As the definitive digital trail expert and content provider, the new AllTrails branding and evolved product experience are among a few of the ways the company is living out its mission 
          to kindle the spirit of adventure as the world’s largest, most inclusive outdoor community. Currently, the platform is home to:"
          />
          <br />
          <li>
            <FormattedMessage defaultMessage="45 million trail-goers" />
          </li>
          <li>
            <FormattedMessage defaultMessage="400K+ curated trails" />
          </li>
          <li>
            <FormattedMessage defaultMessage="700+ million logged miles" />
          </li>
          <br />
          <FormattedMessage
            defaultMessage="With AllTrails, users can plan, live, and share adventures, be it a mountain trek or a stroll in the park. The AllTrails app boasts trail coverage on all seven continents 
          and every country, making it the most recognized digital brand and category-leader in the outdoor activity sector."
          />
        </Typography>
      </>
    )
  },
  {
    cardType: 'press-release',
    title: <FormattedMessage defaultMessage="AllTrails Grows Global Outdoors Platform with Launch of 10 New Languages" />,
    date: 'November 1, 2022',
    modalContent: (
      <>
        <Typography variant="heading400" component="h2">
          <FormattedMessage defaultMessage="AllTrails Grows Global Outdoors Platform with Launch of 10 New Languages" />
        </Typography>
        <Typography variant="heading200" component="h3">
          <FormattedMessage defaultMessage="With 14 languages now available, AllTrails delivers trail mapping app to millions more users, expanding access to the outdoors" />
        </Typography>
        <Typography variant="paragraph100">
          <FormattedMessage
            defaultMessage="{alltrailsLink}, the most trusted and used outdoors platform, today announced the launch of 10 new languages on its global trail app and website. With this expansion, 
            AllTrails is now available in 14 languages, significantly widening the opportunity for the platform’s community of more than 40 million outdoor enthusiasts and 350,000 hiking, running, 
            and mountain biking trails worldwide."
            values={{ alltrailsLink }}
          />
          <br />
          <FormattedMessage
            defaultMessage="The 10 new languages include Danish, Dutch, British English, Italian, Norwegian, Portuguese, Brazilian Portuguese, Polish, Latin American Spanish, and Swedish. 
          These languages add to the four existing languages of American English, French, German, and Spanish. The expansion will contribute to growing the AllTrails global community and make the app easier to use 
          around the globe. Across the regions that recognize these as official languages, there are more than 87,500 hand-curated trails for hikers, bikers, walkers, and runners."
          />
          <br />
          <FormattedMessage
            defaultMessage="“The launch of 10 new languages marks an important milestone for the global growth of the AllTrails community,” said Ron Schneidermann, CEO, AllTrails. “These languages are representative 
          of many of the most active outdoors communities and diverse trail locations; this expansion will only improve users’ experience on our platform. The AllTrails community is at the core of what makes AllTrails such a powerful resource. 
          Launching new languages is another step toward our mission to make the outdoors more accessible to everyone.”"
          />
          <br />
          <FormattedMessage
            defaultMessage="The AllTrails platform helps users find and explore new trails, connect to routes best suited for their unique needs, and get outdoors with the peace of mind provided by navigation tools, as well as 
          reviews and photos from other AllTrails users. With AllTrails, people of all skill levels can discover, record, and share their outdoor experiences. The AllTrails app boasts trail coverage on all seven continents and every country, 
          making it the most recognized digital brand and category-leader in the outdoor activity sector."
          />
          <br />
          <FormattedMessage defaultMessage="The new languages expansion reach many more communities including the following locations and trails:" />
          <br />
          <ul>
            <li>
              <FormattedMessage defaultMessage="Denmark: 5,338 trails" />
            </li>
            <li>
              <FormattedMessage defaultMessage="Netherlands: 6,370 trails" />
            </li>
            <li>
              <FormattedMessage defaultMessage="United Kingdom: 25,369 trails" />
            </li>
            <li>
              <FormattedMessage defaultMessage="Poland: 3,156 trails" />
            </li>
            <li>
              <FormattedMessage defaultMessage="Italy: 11,850 trails" />
            </li>
            <li>
              <FormattedMessage defaultMessage="Sweden: 5,075 trails" />
            </li>
            <li>
              <FormattedMessage defaultMessage="Norway: 6,339 trails" />
            </li>
            <li>
              <FormattedMessage defaultMessage="Portugal: 4,133 trails" />
            </li>
            <li>
              <FormattedMessage defaultMessage="Brazil: 8,222 trails" />
            </li>
            <li>
              <FormattedMessage defaultMessage="Latin America: 11,693 trails" />
              <ul>
                <li>
                  <FormattedMessage defaultMessage="Argentina: 1,290" />
                </li>
                <li>
                  <FormattedMessage defaultMessage="Chile: 1,607" />
                </li>
                <li>
                  <FormattedMessage defaultMessage="Mexico: 6,262" />
                </li>
                <li>
                  <FormattedMessage defaultMessage="Peru: 1,200" />
                </li>
              </ul>
            </li>
          </ul>
        </Typography>
      </>
    )
  },
  {
    cardType: 'press-release',
    title: <FormattedMessage defaultMessage="AllTrails Expands Features Offering, Helping Connect More People to the Outdoors" />,
    date: 'October 01, 2022',
    modalContent: (
      <>
        <Typography variant="heading400" component="h2">
          <FormattedMessage defaultMessage="AllTrails Expands Features Offering, Helping Connect More People to the Outdoors" />
        </Typography>
        <Typography variant="heading200" component="h3">
          <FormattedMessage defaultMessage="New Features Further Improve App Experience and Provide Peace of Mind for Trail-Goers" />
        </Typography>
        <Typography variant="paragraph100">
          <FormattedMessage
            defaultMessage="{alltrailsLink}, the most trusted and used global outdoors platform, today announced several additions to its current suite of features. 
            These additions benefit the brand’s global community by helping them prepare for unique and ever-changing trail conditions, stay safe, and save time before 
            heading outside. With these additions, AllTrails aims to remove barriers to venturing outdoors, and to help its community of more than 40 million outdoor enthusiasts 
            explore its over 350,000 trails with even greater ease."
            values={{ alltrailsLink }}
          />
          <br />
          <FormattedMessage defaultMessage="Inspired by both user feedback and requests, AllTrails is adding five new features to its PRO and Free platforms. These features include:" />
          <br />
          <b>
            <FormattedMessage defaultMessage="AllTrails PRO" />
          </b>
          <li>
            <FormattedMessage
              defaultMessage="Distance Away: This feature addresses what consumers cite as their single largest barrier to getting outside - time. Distance away helps users find nearby 
              trails by filtering for routes within a specified distance of their location. Users can find trails that help them maximize their time outside - spending less time driving and more time on the trail."
            />
          </li>
          <li>
            <FormattedMessage
              defaultMessage="3D Maps: The newest AllTrails PRO feature, 3D Maps provides the user a sense of the trail experience before ever stepping foot outside. The
              feature shows the trail's terrain and elevation to help users know what to expect before hitting the trail."
            />
          </li>
          <br />
          <b>
            <FormattedMessage defaultMessage="AllTrails" />
          </b>
          <li>
            <FormattedMessage
              defaultMessage="Recent Trail Conditions: This new feature is powered by our global community of users, who give insight to whether trails were
              recently muddy, slippery, icy, buggy, or snowy. This information gives users information to plan ahead, and make informed decisions on how
              to prepare, dress, and what gear to bring."
            />
          </li>
          <li>
            <FormattedMessage
              defaultMessage="Estimated Time Remaining: Also inspired by user-requests, this feature eliminates guesswork by helping trail-goers understand how much time
              is left to complete the trail."
            />
          </li>
          <li>
            <FormattedMessage
              defaultMessage="Language Expansion: The AllTrails app and web experience is now also available in 10 new languages/dialects (14 total) including: British
              English, Danish, Dutch, Italian, Norwegian, Polish, Swedish, Portuguese, Brazilian Portuguese, and Latin American Spanish."
            />
          </li>
          <br />
          <FormattedMessage
            defaultMessage="These features are in addition to the platform’s original host of PRO offerings that help keep its community safe and informed while on the
          trail. These include:"
          />
          <br />
          <b>
            <FormattedMessage defaultMessage="AllTrails PRO" />
          </b>
          <li>
            <FormattedMessage defaultMessage="Offline Maps: Users can download maps ahead of time to help stay on track, even without cell service or when reception is lost." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Off-Route Alerts: In the event of a wrong turn, users receive a notification to help quickly course-correct and get back on the right path." />
          </li>
          <li>
            <FormattedMessage defaultMessage="Printed Maps: Unplug completely, and print maps directly from AllTrails or as a backup to technology." />
          </li>
          <br />
          <FormattedMessage
            defaultMessage="The expansion will enhance both the users’ in-app and on-trail experience, helping walkers, bikers, hikers, and runners plan ahead, prepare,
          save time, and stay safe while enjoying the outdoors."
          />
          <br />
          <FormattedMessage
            defaultMessage="“As the most trusted digital guide to the outdoors, we have a responsibility to ensure our global community is safe, prepared, and has a fun
            experience when getting outdoors,” said Ron Schneidermann, CEO of AllTrails. We’ve listened to our users, absorbed their feedback, and are
            thrilled to add five new features that are inspired by their wants and needs. Our goal is to help people of all abilities, levels of
            expertise, and in all corners of the world access the outdoors,” he added."
          />
          <br />
          <FormattedMessage
            defaultMessage="The AllTrails platform helps users find and explore new trails, connect to routes best suited for their unique needs, and get outdoors with
            the peace of mind provided by navigation tools, as well as reviews and photos from other AllTrails users. With AllTrails, people of all
            skill levels can discover, record, and share their outdoor experiences. The AllTrails app boasts trail coverage on all seven continents and
            every country, making it the most recognized digital brand and category-leader in the outdoor activity sector."
          />
        </Typography>
      </>
    )
  },
  {
    cardType: 'press-release',
    title: <FormattedMessage defaultMessage="AllTrails Celebrates 1 Million Paid Subscribers" />,
    date: 'Jan 26, 2021',
    modalContent: (
      <>
        <Typography variant="heading400" component="h2">
          <FormattedMessage defaultMessage="AllTrails Celebrates 1 Million Paid Subscribers" />
        </Typography>
        <Typography variant="heading200" component="h3">
          <FormattedMessage defaultMessage="Milestone Cements the Brand's Vision to Democratize Access to the Outdoors For All" />
        </Typography>
        <Typography variant="paragraph100">
          <FormattedMessage
            defaultMessage="{alltrailsLink}, the most trusted and used digital guide to the outdoors announced a substantial
            milestone - surpassing 1 million paid subscribers for its premium service, AllTrails Pro. Home to the largest collection of hand-curated
            trail guides, the outdoor recreation website and app also maintains a global community of over 25 million registered users. "
            values={{ alltrailsLink }}
          />
          <br />
          <FormattedMessage
            defaultMessage="Even prior to the pandemic, the outdoor sector was experiencing rapid year-over-year growth as more people turned to nature for mental and
            physical health. By May 2020, when pandemic lockdowns loosened, AllTrails saw an accelerated surge of usage as people reconnected with the
            outdoors. The company saw record-breaking growth continue through December, a contrast to the seasonal growth that is typical in the outdoor
            industry. While habit changes during the pandemic further accelerated business performance, the behavior also underscores the impact nature
            has on mental and physical health."
          />
          <br />
          <FormattedMessage
            defaultMessage="“At AllTrails, we believe that everyone has a right to the outdoors, regardless of age, gender, ethnicity, or socioeconomic standing,” said
            Ron Schneidermann, CEO of AllTrails. “Our public lands are for everyone to enjoy and to experience the mental, physical, and emotional
            health benefits that come from spending time outside. This level of growth confirms that we are on the right path in leveraging technology
            to democratize access to the outdoors for all.”"
          />
          <br />
          <FormattedMessage
            defaultMessage="To commemorate the milestone, AllTrails, a 1% For The Planet member, is partnering with One Tree Planted to plant 10 thousand trees - one
            thousand trees for each year AllTrails has served its global community of outdoor enthusiasts."
          />
          <br />
          <FormattedMessage
            defaultMessage="“AllTrails' breakout growth is representative of a larger trend of people adopting new lifestyles and looking to the outdoors,” said Ben
            Spero, managing director at Spectrum Equity, a growth equity firm that is AllTrails' majority shareholder. “It's a rare accomplishment for a
            consumer subscription service to reach 1 million paid subscribers, and this milestone is a testament to the unique service AllTrails
            provides its community in helping connect them to the outdoors and to nature,” he added."
          />
          <br />
          <FormattedMessage defaultMessage="In the past year, AllTrails has achieved:" />
          <br />
          <li>
            <FormattedMessage defaultMessage="One million AllTrails Pro subscribers" />
          </li>
          <li>
            <FormattedMessage defaultMessage="Over 25 million cumulative registered users" />
          </li>
          <li>
            <FormattedMessage defaultMessage="Nearly 130% year-over-year increase in daily active users" />
          </li>
          <li>
            <FormattedMessage defaultMessage="Publishing of nearly 200 thousand trail guides" />
          </li>
          <li>
            <FormattedMessage defaultMessage="Trail coverage in 190 countries, 30 territories, and all 7 continents" />
          </li>
        </Typography>
      </>
    )
  },
  {
    cardType: 'press-release',
    title: <FormattedMessage defaultMessage="AllTrails Offers Best Practices for Getting Outside Responsibly" />,
    date: 'May 07, 2020',
    modalContent: (
      <>
        <Typography variant="heading400" component="h2">
          <FormattedMessage defaultMessage="AllTrails Offers Best Practices for Getting Outside Responsibly" />
        </Typography>
        <Typography variant="heading200" component="h3">
          <FormattedMessage defaultMessage="With parks reopening, the outdoor recreation app shares how to hit the trail for mental and physical health, even while social distancing" />
        </Typography>
        <Typography variant="paragraph100">
          <FormattedMessage
            defaultMessage="As coronavirus restrictions loosen, {alltrailsLink}, the most trusted and used digital guide to the
            outdoors, is sharing ways people can safely get outside for mental and physical health."
            values={{ alltrailsLink }}
          />
          <br />
          <FormattedMessage
            defaultMessage="“There's a lot to adjust to in this new normal, but what hasn't changed are the mental and physical health benefits that we can all achieve
          by simply spending time in nature,” said Ron Schneidermann, CEO of AllTrails. “As a company, we're committed to encouraging everyone that
          time spent outside is an easily accessible yet powerful catalyst for healthy and resilient communities.”"
          />
          <br />
          <FormattedMessage
            defaultMessage="In partnership with its board of medical advisors, AllTrails notes that the following are ways that people can responsibly get outdoors for
          mental and physical health:"
          />
          <br />
          <li>
            <FormattedMessage
              defaultMessage="Practice social distancing guidelines when on the trail. Walk single file to avoid overcrowding the path and keep moving to avoid
            unintentional bottlenecks."
            />
          </li>
          <li>
            <FormattedMessage defaultMessage="Bring a mask or bandana, even if it means only wearing it when passing others." />
          </li>
          <li>
            <FormattedMessage defaultMessage="To limit the spread of the virus, opt for outings on local trails rather than traveling to popular outdoor recreation destinations." />
          </li>
          <li>
            <FormattedMessage
              defaultMessage="Avoid trailheads with extensive parking lots, as these can attract large groups. If parking lots are full, consider exploring another
            trail."
            />
          </li>
          <li>
            <FormattedMessage
              defaultMessage="AllTrails data indicates that trail activity is busiest on the weekends during the late morning and early afternoon. Plan activities
              during quieter hours, such as on weekday mornings."
            />
          </li>
          <br />
          <FormattedMessage defaultMessage="The AllTrails app also has several features that promote responsible social distancing outdoors:" />
          <br />
          <li>
            <FormattedMessage defaultMessage="The trail traffic filter helps users identify trails that are less congested." />
          </li>
          <li>
            <FormattedMessage defaultMessage="The route-type filter locates loops, rather than out-and-back routes, for less contact with other trail-goers." />
          </li>
          <li>
            <FormattedMessage
              defaultMessage="The app also details trail length and difficulty. If users are capable and up for the challenge, harder and longer routes tend to be less
            crowded, particularly as the trail veers further from the trailhead."
            />
          </li>
          <br />
          <FormattedMessage
            defaultMessage="As a champion of the mental and physical health benefits of the outdoors, AllTrails is committed to empowering people with the confidence to
              hit the trail safely and responsibly during this unprecedented time."
          />
        </Typography>
      </>
    )
  },
  {
    cardType: 'press-release',
    title: <FormattedMessage defaultMessage="AllTrails® Acquires Trails.com" />,
    date: 'Jul 18, 2019',
    modalContent: (
      <>
        <Typography variant="heading400" component="h2">
          <FormattedMessage defaultMessage="AllTrails® Acquires Trails.com" />
        </Typography>
        <Typography variant="heading200" component="h3">
          <FormattedMessage defaultMessage="Purchase Marks the Third Acquisition by AllTrails, the Most Comprehensive Global Digital Outdoors Guide Provider" />
        </Typography>
        <Typography variant="paragraph100">
          <FormattedMessage
            defaultMessage="{alltrailsLink}, the most trusted and used digital guide to the outdoors, announced today the
            acquisition of pioneering platform for outdoor recreation, Trails.com. Terms of the transaction were not disclosed."
            values={{ alltrailsLink }}
          />
          <br />
          <FormattedMessage
            defaultMessage="Trails.com provides maps and tips for activities such as hiking, mountain biking, snowshoeing and trail running, assisting trail-goers with
          navigation for outdoor adventures. “We are thrilled to join AllTrails in its effort to equip outdoor enthusiasts with even more content,
          help people get outside and enjoy nature,” said Howard Love, CEO of LoveToKnow, Corp, Trails.com's holding company. Trails.com is AllTrails'
          third acquisition and strengthens its growing library of over 75 thousand hand curated trail maps."
          />
          <br />
          <FormattedMessage
            defaultMessage="“At AllTrails, we are committed to helping people all over the world, in both urban and rural communities, get outside, and see all that
          nature has to offer,” said Jade Van Doren, CEO of AllTrails. “Whether it's a quick stroll to help decompress or a backpacking adventure
          through the wilderness, we want to make the outdoors safer and more accessible for all. This acquisition helps us serve even more people in
          their quest to enjoy the outdoors.”"
          />
          <br />
          <FormattedMessage
            defaultMessage="Trails.com is AllTrails' second acquisition of 2019. AllTrails acquired UK based trails app, iFootpath, in April 2019, and EveryTrail in
          2016. The company also recently received a $75 million investment from Spectrum Equity in October 2018 to help grow its overseas presence
          and expand trail data."
          />
        </Typography>
      </>
    )
  },
  {
    cardType: 'press-release',
    title: <FormattedMessage defaultMessage="AllTrails Acquires iFootpath" />,
    date: 'Apr 24, 2019',
    modalContent: (
      <>
        <Typography variant="heading400" component="h2">
          <FormattedMessage defaultMessage="AllTrails Acquires iFootpath" />
        </Typography>
        <Typography variant="heading200" component="h3">
          <FormattedMessage defaultMessage="Acquisition Makes AllTrails the Most Comprehensive Global Digital Outdoors Guide Provider" />
        </Typography>
        <Typography variant="paragraph100">
          <FormattedMessage
            defaultMessage="{alltrailsLink}, the most trusted and used digital guide to the outdoors, announced today the
            acquisition of iFootpath, the #1 paid travel app in the UK. Terms of the transaction were not disclosed. "
            values={{ alltrailsLink }}
          />
          <br />
          <FormattedMessage
            defaultMessage="“iFootpath's mission is to make walking in the UK fun, accessible and stress-free,” explained Claire Sharp and Richard Jemmett, Co-Founders
          of iFootpath. “We're excited to be working with AllTrails to make our walking guides available to an even wider audience.”"
          />
          <br />
          <FormattedMessage
            defaultMessage="Since its founding in 2011, iFootpath has published over 1300 hand-curated guides focusing on walking trails in the UK. Each guide includes
          a detailed map along with turn-by-turn directions, waypoints, photos, and driving directions to the trailhead."
          />
          <br />
          <FormattedMessage
            defaultMessage="“Our mission is to help people all over the world spend more time enjoying the outdoors,” explained Jade Van Doren, CEO of AllTrails. “By
          adding iFootpath's extensive data to the AllTrails platform, we have made it easier than ever for both locals and tourists to explore all
          that the United Kingdom has to offer.”"
          />
          <br />
          <FormattedMessage defaultMessage="iFootpath is AllTrails' second acquisition, following EveryTrail in 2016." />
        </Typography>
      </>
    )
  },
  {
    cardType: 'press-release',
    title: <FormattedMessage defaultMessage="Spectrum Equity Leads New Investment and Majority Recapitalization of AllTrails" />,
    date: 'Oct 11, 2018',
    modalContent: (
      <>
        <Typography variant="heading400" component="h2">
          <FormattedMessage defaultMessage="Spectrum Equity Leads New Investment and Majority Recapitalization of AllTrails" />
        </Typography>
        <Typography variant="heading200" component="h3">
          <FormattedMessage defaultMessage="Investment will foster continued growth of global digital outdoors guide provider" />
        </Typography>
        <Typography variant="paragraph100">
          <FormattedMessage
            defaultMessage="{spectrumEquityLink} announced that it led a substantial new investment in AllTrails, the most trusted and used digital guide to the outdoors, to help accelerate
            product development, add more trail content around the world and grow the company's user base. As part of the transaction, Spectrum acquired
            a majority stake in AllTrails, in partnership with the company's CEO Jade Van Doren, and COO Ron Schneidermann. "
            values={{ spectrumEquityLink }}
          />
          <br />
          <FormattedMessage
            defaultMessage="AllTrails helps over 9 million registered users discover, record and share their outdoor experiences, including hiking, mountain biking and
          trail running. AllTrails has created the largest collection of trail information globally with over 55,000 hand-curated trails in 102
          different countries. The company has grown rapidly over the past few years without significant outside funding."
          />
          <br />
          <FormattedMessage defaultMessage="In conjunction with the transaction, Ben Spero and Matt Neidlinger from Spectrum Equity will join the board of directors." />
          <br />
          <FormattedMessage
            defaultMessage="“We are thrilled to partner with Jade, Ron and the entire AllTrails team to support the company in its next phase of growth,” said Ben
          Spero, Managing Director of Spectrum Equity. “The company is helping millions of people discover great outdoor activities and lead
          healthier, active lifestyles.”"
          />
          <br />
          <FormattedMessage
            defaultMessage="“Spectrum's extensive experience with freemium, subscription-based businesses was particularly attractive to us in considering potential
          financial partners,” said Jade Van Doren, CEO of AllTrails. “We're thrilled to have Spectrum as our new partner as we continue to innovate
          on our products, expand our trail coverage around the world and support our passionate community of users.”"
          />
          <br />
          <FormattedMessage
            defaultMessage="Matt Neidlinger from Spectrum Equity added, “As we've gotten to know Jade, Ron and the team over the past few years, we've been impressed by
          their ability to scale the platform into the most-recognized, category-leading brand in the space.”"
          />
          <br />
          <FormattedMessage
            defaultMessage="AllTrails was advised by Stubbs Alderton & Markiles with GP Bullhound serving as AllTrails' exclusive financial advisor. Spectrum Equity was
          advised by Latham & Watkins."
          />
          <br />
          <FormattedMessage defaultMessage="Financial terms of the transaction were not disclosed." />
        </Typography>
      </>
    )
  },
  {
    cardType: 'press-release',
    title: <FormattedMessage defaultMessage="AllTrails® Acquires German Trails App GPSies" />,
    date: 'Jul 30, 2019',
    modalContent: (
      <>
        <Typography variant="heading400" component="h2">
          <FormattedMessage defaultMessage="AllTrails® Acquires German Trails App GPSies" />
        </Typography>
        <Typography variant="heading200" component="h3">
          <FormattedMessage defaultMessage="Purchase Solidifies Brand's Global Presence and Expands European Trail Network" />
        </Typography>
        <Typography variant="paragraph100">
          <FormattedMessage
            defaultMessage="{alltrailsLink}, the most trusted and used digital guide to the outdoors, announced today the
            acquisition of German-based outdoor recreation platform, GPSies. The deal further enhances AllTrails' trail coverage across Europe and
            cements its position as the most comprehensive global resource for outdoor recreation. "
            values={{ alltrailsLink }}
          />
          <br />
          <FormattedMessage
            defaultMessage="Since its founding in 2006, GPSies has helped hikers, walkers and cyclists across the EU plan outdoor activities with a robust library of
          user generated GPS recordings along with a powerful route planning tool for creating downloadable custom maps. “We are delighted to join
          AllTrails in our mutual goal of helping people embrace the outdoors,” said Klaus Bechtold, CEO of GPSies. “Together we can bring even more
          routes, services and information to a growing audience of people who are looking to spend more time outside enjoying nature.”"
          />
          <br />
          <FormattedMessage
            defaultMessage="As part of the company's effort to better serve trail-goers across the globe, AllTrails recently launched translated versions of its mobile
          apps and website, making all of its content available in German, French, and Spanish."
          />
          <br />
          <FormattedMessage
            defaultMessage="“Time spent in the outdoors is beneficial for everyone, improving both our physical and mental well being,” said Jade Van Doren, CEO of
          AllTrails. “By continuing to expand our presence in Europe, we are not only fulfilling our goal of helping people access nature more easily
          and safely, but also making a positive impact on people's health.”"
          />
          <br />
          <FormattedMessage
            defaultMessage="GPSies is AllTrails' fourth acquisition; the company most recently acquired Trails.com in July 2019, UK based trails app iFootpath in April
          2019, and EveryTrail in 2016. The company also recently received a $75 million investment from Spectrum Equity in October 2018 to help grow
          its overseas presence and expand trail data."
          />
        </Typography>
      </>
    )
  },
  {
    cardType: 'press-release',
    title: <FormattedMessage defaultMessage="AllTrails Gives Trail Users Peace of Mind With Launch of Lifeline" />,
    date: 'Aug 28, 2018',
    modalContent: (
      <>
        <Typography variant="heading400" component="h2">
          <FormattedMessage defaultMessage="AllTrails Gives Trail Users Peace of Mind With Launch of Lifeline" />
        </Typography>
        <Typography variant="heading200" component="h3">
          <FormattedMessage defaultMessage="Live Location Tracking Feature Keeps Loved Ones Informed" />
        </Typography>
        <Typography variant="paragraph100">
          <FormattedMessage
            defaultMessage="{alltrailsLink}, the most trusted and used digital guide to the outdoors, today announced the
            release of a new feature called Lifeline to its number one-rated mobile app for iOS and Android. Designed to put outdoor enthusiasts and
            their loved ones at ease, AllTrails Pro subscribers can now provide designated safety contacts with a detailed map of their planned route,
            status updates and automated emergency alerts while out on the trail. If the user's phone has a data connection, safety contacts will even
            be able to follow along with real-time location tracking."
            values={{ alltrailsLink }}
          />
          <br />
          <FormattedMessage
            defaultMessage="“The addition of Lifeline to our mobile platform is a huge step in ensuring AllTrails users feel connected and confident while exploring in
          the outdoors,” said Jade Van Doren, CEO of AllTrails. “Made for people heading out on their own, and for those exploring in more remote
          areas, we felt it was important to provide the peace of mind and security that comes from your loved ones knowing your plan, where you are,
          and getting automatic alerts if something goes wrong.”"
          />
          <br />
          <FormattedMessage
            defaultMessage="With the new Lifeline feature AllTrails Pro subscribers can select up to five contacts to receive a unique tracking URL to view their i)
          planned route on an easy-to-read map, ii) planned starting and ending locations iii) planned start and finish time, iv) total tracked
          distance and elevation gain, and v) real-time location on a map plotted against the planned route."
          />
          <br />
          <FormattedMessage
            defaultMessage="Once a user completes their activity out on the trail and marks themself as safe, their safety contacts will be notified. If an AllTrails
          Pro subscriber is overdue for their scheduled finish time, safety contacts will automatically receive an alert with the user's last known
          GPS coordinates. Safety contacts do not have to be AllTrails users or subscribers to watch the user's progress on the trail and receive
          alerts."
          />
          <br />
          <FormattedMessage
            defaultMessage="Similar to other location tracking mobile apps, Lifeline does require a data signal when setting up the feature and real-time tracking is
          dependent on a data connection. Many features of the AllTrails app are available with a free account, and AllTrails Pro can be purchased for
          $29.99/year, or $99.99/lifetime."
          />
          <br />
          <FormattedMessage
            defaultMessage="For more information on {alltrailsLink} or the AllTrails {iosLink} or {androidLink} mobile app please contact {pressLink}."
            values={{ alltrailsLink, iosLink, androidLink, pressLink }}
          />
        </Typography>
      </>
    )
  },
  {
    cardType: 'press-release',
    title: <FormattedMessage defaultMessage="AllTrails Celebrates One Million Mobile Installs" />,
    date: 'Nov 07, 2012',
    modalContent: (
      <>
        <Typography variant="heading400" component="h2">
          <FormattedMessage
            defaultMessage="AllTrails, the Leading Digital Network for Outdoor Enthusiasts, Celebrates One Million Mobile Installs and Announces the Retail Launch of
          The Ultimate Outdoor MapKit"
          />
        </Typography>
        <Typography variant="paragraph100">
          <FormattedMessage
            defaultMessage="{alltrailsLink}, the leading digital network focused on outdoor enthusiasts, announced today that
            it has seen more than 1 million installs of its industry-leading mobile applications, as well as the launch of its premium subscription
            service in over 200 retailers nationwide including REI, EMS, Sport Chalet and Bass Pro Shops. "
            values={{ alltrailsLink }}
          />
          <br />
          <FormattedMessage
            defaultMessage="AllTrails released its first mobile application in 2011, which quickly became one of the top outdoor apps for the iPhone. AllTrails for
          Android was released one year ago, and has quickly surged in popularity in the Google Play store — garnering a spot among the Top 25 Travel
          Apps and over half a million installs."
          />
          <br />
          <FormattedMessage
            defaultMessage="“The growth we are seeing on mobile is tremendous,” says Justin Ip, Head of Product at AllTrails. “When we launched our Android app last
          year we never dreamt that it would so quickly overtake the huge head start of our iPhone application. In one year, we have seen half a
          million users download the app through Google Play. I believe our growing base of mobile app users will be delighted by the pace of
          innovation we have planned for the platform this coming year.”"
          />
          <br />
          <FormattedMessage
            defaultMessage="The rapid adoption of smartphones was a major driving force behind bringing the AllTrails premium subscription service to retail. The
          product will be sold under the name “The Ultimate Outdoor MapKit” and will be co-branded under both the AllTrails and National Geographic
          brands. Customers purchasing MapKit through their favorite outdoor retailer will be able to redeem their purchase for 1 year of access to
          AllTrails Pro. This provides them with unlimited access to National Geographic TOPO! and Trails Illustrated maps, as well as advanced
          trip-planning tools which can be accessed online through a web browser, and offline on a smartphone or by printing maps on-demand."
          />
          <br />
          <FormattedMessage
            defaultMessage="“It's not often that a software startup gets the opportunity to help design packaging and take a physical product to market,” says Russell
          Cook, Founder & CEO of AllTrails. “The retail expertise of our partners at National Geographic has been invaluable, and we have been
          overwhelmed by the positive response of retailers. Retailers are seeing consumers do more on their mobile devices and they are looking for
          products like MapKit that allow them to better serve those customers. We're excited to be working with National Geographic and their
          retailer partners to provide outdoor enthusiasts seamless access to outdoor recreation maps and information, whether online or offline.”"
          />
        </Typography>
      </>
    )
  }
];

export default pressReleases;
